import { makeStyles, Typography } from "@material-ui/core";
import { useQuery } from "@tanstack/react-query";
import { getFilteredEmployeeSnapshot } from "crud/rent.crud";
import { useModal } from "hooks/useModal";
import { useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import StepContainer from "views/admin/RENT/Create/Steps/step-container";
import EmployeesTable from "./employee-table";
import EmployeesTableFilter from "./employee-table-filter";
import { EmployeeModal } from "./Modal/employee-modal";

const useStyles = makeStyles((theme) => ({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

export default function EmployeesInfoTab() {
  const { id } = useParams();

  const defaultFormValues = {
    name: "",
    ssn: "",
    nif: "",
    documentNumber: "",
    startDate: null,
    endDate: null,
    workCenterId: "",
    professionalCategoryId: "",
    employmentStatusId: "",
    pageIndex: 1,
    pageSize: 15,
  };

  const form = useForm({
    mode: "onChange",
    defaultValues: defaultFormValues,
  });

  const { isOpen, modalData, openModal, closeModal } = useModal();

  return (
    <>
      <StepContainer title="Dados de Trabalhadores">
        <FormProvider {...form}>
          <EmployeesInfoTabInner id={id} defaultFormValues={defaultFormValues} openModal={openModal} />
        </FormProvider>
      </StepContainer>

      {isOpen && <EmployeeModal isOpen={isOpen} closeModal={closeModal} modalData={modalData} />}
    </>
  );
}

function EmployeesInfoTabInner({ id, defaultFormValues, openModal }) {
  const classes = useStyles();
  const { getValues, setValue, watch, reset } = useFormContext();
  const submittedFilterRef = useRef(null);

  const pageIndex = watch("pageIndex") ?? 1;
  const pageSize = watch("pageSize") ?? 15;

  const buildPayload = (values, override) => {
    const effectivePageIndex = override?.pageIndex ?? values.pageIndex ?? 1;
    const effectivePageSize = override?.pageSize ?? values.pageSize ?? 15;

    return {
      id,
      name: values.name ?? "",
      ssn: values.ssn ?? "",
      nif: values.nif ?? "",
      documentNumber: values.documentNumber ?? "",
      startDate: values.startDate ? values.startDate.format() : null,
      endDate: values.endDate ? values.endDate.format() : null,
      workCenterId: values.workCenterId ?? "",
      professionalCategoryId: values.professionalCategoryId ?? "",
      employmentStatusId: values.employmentStatusId ?? "",
      pageIndex: effectivePageIndex,
      pageSize: effectivePageSize,
    };
  };

  const [queryParams, setQueryParams] = useState(() => buildPayload(getValues()));

  const employeesQuery = useQuery({
    queryKey: ["rent", "employeesSnapshot", id, queryParams],
    queryFn: () => getFilteredEmployeeSnapshot(queryParams),
    enabled: !!id && !!queryParams,
    keepPreviousData: true,
    select: (response) => {
      if (!response) return { data: [], totalCount: 0, totalPages: 0 };

      return {
        data: response.data,
        totalCount: response.totalCount,
        totalPages: response.totalPages,
      };
    },
  });

  return (
    <div className={classes.form}>
      <EmployeesTableFilter
        onSubmit={() => {
          const values = getValues();

          const next = buildPayload(values, {
            pageIndex: 1,
            pageSize: values.pageSize ?? defaultFormValues.pageSize ?? 15,
          });

          submittedFilterRef.current = next;
          setQueryParams(next);

          if ((values.pageIndex ?? 1) !== 1) {
            setValue("pageIndex", 1);
          }
        }}
        onReset={() => {
          reset(defaultFormValues);

          const next = buildPayload(defaultFormValues, {
            pageIndex: 1,
            pageSize: defaultFormValues.pageSize,
          });
          submittedFilterRef.current = next;
          setQueryParams(next);
        }}
      />

      <Typography style={{ fontWeight: 600 }} component="p" variant="body2">
        Na tabela em baixo estão listados todos os trabalhadores que foram submetidos na criação do relatório RENT.
        Poderá editar as informações entrando no detalhe de cada trabalhador. Após gravar ou adicionar o trabalhador,
        a lista de trabalhadores no menu utilizadores será actualizada.
      </Typography>

      <EmployeesTable
        handleOpenDetails={openModal}
        data={employeesQuery.data}
        isFetching={employeesQuery.isFetching}
        pageIndex={pageIndex}
        onPageChange={(_, value) => {
          setValue("pageIndex", value);

          const base = submittedFilterRef.current ?? buildPayload(getValues());
          const next = {
            ...base,
            pageIndex: value,
            pageSize,
          };
          submittedFilterRef.current = next;
          setQueryParams(next);
        }}
      />
    </div>
  );
}
